// @spec SHL-STR-033
'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  /**
   * Whether the modal is open.
   */
  open: boolean;

  /**
   * Called when the modal open state changes.
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Modal title displayed in the header.
   */
  title: string;

  /**
   * Optional description displayed below the title.
   */
  description?: string;

  /**
   * Modal size variant.
   * @default 'md'
   */
  size?: ModalSize;

  /**
   * Modal content (body).
   */
  children: React.ReactNode;

  /**
   * Optional footer content (typically action buttons).
   * If not provided, no footer is rendered.
   */
  footer?: React.ReactNode;

  /**
   * Whether to show the close button (X) in the header.
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Additional className for the DialogContent.
   */
  className?: string;

  /**
   * Whether to prevent closing when clicking outside the modal.
   * @default false
   */
  preventOutsideClose?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]',
};

/**
 * Modal component that provides a standardized dialog pattern.
 *
 * Built on top of Radix Dialog primitives, this component provides:
 * - Pre-configured size variants (sm, md, lg, xl, full)
 * - Consistent header with title and optional description
 * - Optional footer for action buttons
 * - Accessibility features (focus trap, ARIA attributes)
 * - Keyboard handling (Escape to close)
 *
 * @example
 * <Modal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Confirm Action"
 *   description="Are you sure you want to proceed?"
 *   footer={
 *     <ModalActions>
 *       <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *     </ModalActions>
 *   }
 * >
 *   <p>Modal content here</p>
 * </Modal>
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  children,
  footer,
  showCloseButton = true,
  className,
  preventOutsideClose = false,
}: ModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/80',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%]',
            'gap-4 border bg-background p-6 shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'sm:rounded-lg',
            sizeClasses[size],
            className
          )}
          onInteractOutside={(e) => {
            if (preventOutsideClose) {
              e.preventDefault();
            }
          }}
        >
          {/* Header */}
          <div className="flex flex-col space-y-1.5 text-center sm:text-left">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                {description}
              </DialogPrimitive.Description>
            )}
          </div>

          {/* Body */}
          <div className="max-h-[60vh] overflow-y-auto">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              {footer}
            </div>
          )}

          {/* Close button */}
          {showCloseButton && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface ModalActionsProps {
  /**
   * Actions content (buttons).
   */
  children: React.ReactNode;

  /**
   * Additional className for the footer container.
   */
  className?: string;
}

/**
 * A styled wrapper for modal footer actions that provides consistent spacing and alignment.
 *
 * @example
 * <ModalActions>
 *   <Button variant="outline">Cancel</Button>
 *   <Button>Save</Button>
 * </ModalActions>
 */
export function ModalActions({ children, className }: ModalActionsProps) {
  return <div className={cn('flex justify-end gap-2', className)}>{children}</div>;
}
